import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Grid,
  Card,
  CardActionArea,
  Typography,
  Stack,
  Box,
  useTheme,
} from '@mui/material';

import {
  IcArticle,
  IcBuildingInspection,
  IcCalendar,
  IcCreditCard,
  IcDistance,
  IcFamiliarFaceZone,
  IcLocalPolice,
  IcLocalShipping,
  IcChromeReaderMode,
  IcNews,
  IcPhotoCamera,
  IcSupplies,
  IcWarningNew,
  IcPersonShield,
} from '@/assets/images';
import { IUser, Role } from '@/models';
import { isAnyTargetRole } from '@/services';
import { useUserStore } from '@/store';

interface ToolItem {
  key: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  isDemoMode?: boolean;
}

export const ToolsManagementGrid: FC = () => {
  const navigate = useNavigate();
  const { account, user } = useUserStore();
  const theme = useTheme();

  const canViewWantedPerson = isAnyTargetRole(user as IUser, [
    Role.WANTED_PERSON_VIEW,
    Role.WANTED_PERSON_VIEW_ADD_ATTEMPT,
    Role.WANTED_PERSON_EDIT,
  ]);

  const canViewLocationMarkers = isAnyTargetRole(user as IUser, [
    Role.DISPATCHER,
    Role.FIRST_RESPONDER,
  ]);

  const canViewOtherItems = isAnyTargetRole(user as IUser, [
    Role.TOOLS_MANAGER,
    Role.FIRST_RESPONDER,
    Role.DISPATCHER,
  ]);

  const canViewDIR = isAnyTargetRole(user as IUser, [
    Role.ACCOUNT_ADMIN,
    Role.RMS_CASES_FULL_ACCESS,
    Role.RMS_CASES_VIEW,
    Role.AGENCY_ADMIN,
    Role.KASE_APPROVER,
  ]);

  const canViewInternalAffairs = isAnyTargetRole(user as IUser, [
    Role.IA_OFFICER,
  ]);

  const canViewSexOffenders = isAnyTargetRole(user as IUser, [
    Role.SEX_OFFENDER_MANAGE,
    Role.TOOLS_MANAGER,
  ]);

  const items: ToolItem[] = useMemo(
    () => [
      {
        key: 'sex-offenders',
        title: 'Sex Offenders',
        description: 'Manage sex offender registry and tracking',
        icon: IcLocalPolice,
        path: '/tools/sex-offenders',
      },
      {
        key: 'cameras',
        title: 'Cameras',
        description: 'Access and manage surveillance cameras',
        icon: IcPhotoCamera,
        path: '/tools/cameras',
        isDemoMode: true,
      },
      {
        key: 'bolo',
        title: 'BOLO',
        description: 'Be On the Lookout alerts and notices',
        icon: IcWarningNew,
        path: '/tools/bolo',
        isDemoMode: true,
      },
      {
        key: 'complaints',
        title: 'Complaints',
        description: 'Handle citizen complaints and reports',
        icon: IcNews,
        path: '/tools/complaints',
        isDemoMode: true,
      },
      {
        key: 'permit',
        title: 'Permit',
        description: 'Process permits and licensing requests',
        icon: IcCreditCard,
        path: '/tools/permit',
        isDemoMode: true,
      },
      {
        key: 'wanted-person',
        title: 'Wanted Persons',
        description: 'Manage wanted persons database',
        icon: IcFamiliarFaceZone,
        path: '/tools/wanted-person',
      },
      {
        key: 'calendar',
        title: 'Calendar And Schedules',
        description: 'View and manage department schedules and events',
        icon: IcCalendar,
        path: '/tools/calendar-scheduler',
        isDemoMode: false,
      },
      {
        key: 'vehicle-maintenance',
        title: 'Vehicle Maintenance',
        description: 'Track and manage fleet vehicle maintenance',
        icon: IcLocalShipping,
        path: '/tools/vehicle-maintenance',
        isDemoMode: false,
      },
      {
        key: 'location-markers',
        title: 'Location Markers',
        description: 'View, manage, and track marked locations on the map',
        icon: IcDistance,
        path: '/tools/location-markers',
      },
      {
        key: 'towing',
        title: 'Towing Management',
        description: 'Handle towing operations and storage details',
        icon: IcSupplies,
        path: '/tools/towing-management',
        isDemoMode: true,
      },
      {
        key: 'building-inspection',
        title: 'Building Inspections',
        description: 'Manage and track building inspections.',
        icon: IcBuildingInspection,
        path: '/tools/building-inspection',
        isDemoMode: true,
      },
      {
        key: 'internal-affairs',
        title: 'Internal Affairs',
        description: 'Investigate staff conduct and manage internal cases',
        icon: IcPersonShield,
        path: '/tools/internal-affairs',
        isDemoMode: true,
      },
      {
        key: 'domestic-incident-reports',
        title: 'Domestic Incident Reports',
        description: 'Create and manage DIRs independently from cases',
        icon: IcChromeReaderMode,
        path: '/tools/domestic-incident-reports',
        isDemoMode: false,
      },
      {
        key: 'law-library',
        title: 'Law Library',
        description: 'View and edit law text for NYS offense codes',
        icon: IcArticle,
        path: '/tools/law-library',
        isDemoMode: false,
      },
    ],
    [],
  );

  const filteredItems = items.filter((item) => {
    if (item.key === 'wanted-person') {
      return canViewWantedPerson;
    }
    if (item.key === 'location-markers') {
      return canViewLocationMarkers;
    }

    if (item.key === 'vehicle-maintenance') {
      return true;
    }

    if (item.key === 'sex-offenders') {
      return canViewSexOffenders;
    }

    if (item.key === 'internal-affairs') {
      return canViewInternalAffairs;
    }

    if (item.key === 'domestic-incident-reports') {
      return canViewDIR;
    }

    if (item.key === 'law-library') {
      return true;
    }

    return (
      (!item.isDemoMode && canViewOtherItems) ||
      account?.settings?.demoMode === true
    );
  });

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        overflowY: 'auto',
        mt: 3,
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{
          maxWidth: 208 * 3 + 24 * 2,
        }}
      >
        {filteredItems.map((item) => {
          const Icon = item.icon;
          return (
            <Grid
              item
              key={item.key}
              xs={12}
              sm={6}
              md={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Card
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 208,
                  height: 160,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: 0.4,
                  },
                }}
              >
                <CardActionArea
                  sx={{ height: '100%', p: 2 }}
                  onClick={() => navigate(item.path)}
                >
                  <Stack spacing={2} alignItems="flex-start">
                    <Stack
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '6px',
                        backgroundColor:
                          theme.palette.mode !== 'light'
                            ? '#233038'
                            : '#F5F7F7',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 28, color: 'text.secondary' }} />
                    </Stack>
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontStyle: 'normal',
                          fontSize: '15px',
                          lineHeight: '20px',
                          letterSpacing: 0,
                          color: 'text.primary',
                        }}
                        noWrap
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '20px',
                          letterSpacing: 0,
                          color: 'text.secondary',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ToolsManagementGrid;
